// Modal wrapper with focus trapping and close controls.
import { HorizBox, Pad, PadBox, Separator } from "./basics";
import { BreadCrumb, ButtonType, CTAButton } from "./button";
import { ScrollView, StyleSheet, View } from 'react-native';
import { DocumentLevelComponent } from "platform-specific/popup";
import { Close } from '@carbon/icons-react';
import { FocusLoopView } from "system/focustrap";
import { nullElement, t, TLabel } from "util/stdtypes";
import { colorButtonSecondary, colorSurfacePrimary, colorTextPrimary, colorTextSecondary } from "./color";
import { Heading, Paragraph } from "./text";
import React from 'react';

export function Modal({children, buttonRow=nullElement, onClose, showTopBar=true}) {
    const s = ModalStyle;
    return <DocumentLevelComponent>
        <View style={s.outer} testID="popup-content">
            <FocusLoopView onCloseLoop={() => onClose()} index={1}>
                <View style={s.inner}>
                    {showTopBar && <>
                        <HorizBox spread center>
                            <Pad size={56} />
                            <PadBox right={12}><BreadCrumb ariaLabel={t('Close')} testID='close-modal' icon={Close} iconProps={{ size: 32, color: colorTextPrimary }} onPress={onClose} /></PadBox>
                        </HorizBox>
                        <Separator />
                    </>}
                    <ScrollView>
                        {children}
                    </ScrollView>
                    {buttonRow && <Pad />}
                    {buttonRow && <Separator />}
                    {buttonRow && <PadBox horiz={20} vert={20}>{buttonRow}</PadBox>}
                </View>
            </FocusLoopView>
        </View>
    </DocumentLevelComponent>
}

// DO NOT export - will replace Modal but has styling changes that need to be tested
function NewModal({children, buttonRow=nullElement, onClose, showTopBar=true}) {
    const s = ModalStyle;
    return <DocumentLevelComponent>
        <View style={s.outer} testID="popup-content">
            <FocusLoopView onCloseLoop={() => onClose()} index={1}>
                <View style={s.inner}>
                    {showTopBar && <>
                        <HorizBox spread center>
                            <Pad size={56} />
                            <PadBox right={12}><BreadCrumb ariaLabel={t('Close')} testID='close-modal' icon={Close} iconProps={{ size: 32, color: colorTextPrimary }} onPress={onClose} /></PadBox>
                        </HorizBox>
                        <Separator />
                    </>}
                    <ScrollView>
                        {children}
                    </ScrollView>
                    {buttonRow && <PadBox horiz={20} bottom={32} top={0}>{buttonRow}</PadBox>}
                </View>
            </FocusLoopView>
        </View>
    </DocumentLevelComponent>
}

const ModalStyle = StyleSheet.create({
    outer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    inner: {
        width: '100%',
        // maxHeight: 600,
        maxWidth: 400,
        borderRadius: 8,
        flexShrink: 1,
        backgroundColor: colorSurfacePrimary,
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
    }
});

type FunctionalModalProps = {
    onClose: () => void;
    onConfirm: () => void;
    headingLabel?: TLabel;
    headingText?: string;
    descriptionLabel?: TLabel;
    descriptionText?: string;
    confirmButton?: {
        label?: TLabel;
        text?: string;
        type?: ButtonType;
        icon?: JSX.Element;
    }
}

export function FunctionalModal({
    onClose,
    onConfirm,
    headingLabel,
    headingText,
    descriptionLabel,
    descriptionText,
    confirmButton = {label: t('Confirm'), text: 'Confirm', type: 'primary', icon: undefined},
} : FunctionalModalProps) {
    const buttonRow = (
        <HorizBox spread center gap={16}>
            <CTAButton wide type='secondary' label={t('Cancel')} onPress={onClose} />
            <CTAButton wide
                type={confirmButton.type}
                label={confirmButton.label}
                text={confirmButton.text}
                onPress={onConfirm}
                icon={confirmButton.icon}
            />
        </HorizBox>
    );
    return (
        <NewModal onClose={onClose} showTopBar={false} buttonRow={buttonRow}>
            <PadBox horiz={32} vert={32}>
                <Heading type='large' weight='strong' label={headingLabel} text={headingText} />
                <Pad size={12} />
                <Paragraph color={colorTextSecondary} label={descriptionLabel} text={descriptionText} />
            </PadBox>
        </NewModal>
    )
}
